import React, { useState, useEffect } from 'react';
import { getProgressionSummary, getExerciseProgression } from '../../services/fitnessService';
import { format, parseISO } from 'date-fns';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { gymKgToDisplay } from '../../utils/weightUnits';
import './ProgressionSection.css';

const ProgressionSection = () => {
  const weightUnit = useWeightUnit();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (expanded && !summary) {
      fetchSummary();
    }
  }, [expanded]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const data = await getProgressionSummary();
      setSummary(data);
    } catch (error) {
      console.error('Error fetching progression:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandExercise = async (exerciseId) => {
    if (expandedExercise === exerciseId) {
      setExpandedExercise(null);
      setDetailData(null);
      return;
    }

    setExpandedExercise(exerciseId);
    setDetailLoading(true);
    try {
      const data = await getExerciseProgression(exerciseId);
      setDetailData(data);
    } catch (error) {
      console.error('Error fetching exercise progression:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatWeight = (w) => {
    const display = gymKgToDisplay(w, weightUnit);
    return display.value !== '' ? `${display.value} ${display.unit}` : '—';
  };
  const formatVolume = (v) => v > 0 ? v.toLocaleString() : '—';

  const getTrendIcon = (change) => {
    if (change > 0) return { icon: 'fa-arrow-up', cls: 'trend-up', label: `+${change}` };
    if (change < 0) return { icon: 'fa-arrow-down', cls: 'trend-down', label: `${change}` };
    return { icon: 'fa-minus', cls: 'trend-neutral', label: 'No change' };
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'MMM d'); } catch { return '—'; }
  };

  return (
    <div className="progression-section">
      <div className="progression-header" onClick={() => setExpanded(!expanded)}>
        <h2><i className="fas fa-chart-line"></i> Progression Tracking</h2>
        <i className={`fas fa-chevron-down progression-toggle ${expanded ? 'open' : ''}`}></i>
      </div>

      {expanded && (
        <div className="progression-body">
          {loading ? (
            <div className="progression-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading progression data...
            </div>
          ) : !summary || summary.exercises.length === 0 ? (
            <div className="progression-empty">
              <i className="fas fa-dumbbell"></i>
              Complete workouts to start tracking your progression!
            </div>
          ) : (
            <>
              <div className="progression-grid">
                {summary.exercises.map(ex => {
                  const rawChange = Number(ex.weightChange) || 0;
                  const convertedAbs = rawChange === 0
                    ? 0
                    : gymKgToDisplay(Math.abs(rawChange), weightUnit).value;
                  const displayChange = rawChange === 0
                    ? 0
                    : (rawChange > 0 ? convertedAbs : -convertedAbs);
                  const weightTrend = getTrendIcon(displayChange);
                  const isOpen = expandedExercise === ex.exerciseId;

                  return (
                    <div
                      key={ex.exerciseId}
                      className="progression-card"
                      onClick={() => handleExpandExercise(ex.exerciseId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="progression-card-header">
                        <h3>{ex.exerciseName}</h3>
                        <span className="progression-session-count">
                          {ex.sessionCount} session{ex.sessionCount !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="progression-card-stats">
                        <div className="progression-stat">
                          <span className="progression-stat-value">{formatWeight(ex.latestWeight)}</span>
                          <span className="progression-stat-label">Weight</span>
                        </div>
                        <div className="progression-stat">
                          <span className="progression-stat-value">{ex.latestReps}</span>
                          <span className="progression-stat-label">Reps</span>
                        </div>
                        <div className="progression-stat">
                          <span className="progression-stat-value">{formatVolume(ex.latestVolume)}</span>
                          <span className="progression-stat-label">Volume</span>
                        </div>
                      </div>

                      <div className={`progression-card-trend ${weightTrend.cls}`}>
                        <i className={`fas ${weightTrend.icon}`}></i>
                        <span>{weightTrend.label} {weightUnit} since first session</span>
                      </div>

                      <div className="progression-card-dates">
                        {formatDate(ex.firstSessionDate)} — {formatDate(ex.lastSessionDate)}
                      </div>

                      {isOpen && (
                        <div className="progression-detail" onClick={(e) => e.stopPropagation()}>
                          {detailLoading ? (
                            <div className="progression-loading">
                              <i className="fas fa-spinner fa-spin"></i>
                            </div>
                          ) : detailData?.dataPoints ? (
                            <div className="progression-data-points">
                              {detailData.dataPoints.map((dp, i) => (
                                <div key={i} className="data-point">
                                  <span className="data-point-date">{formatDate(dp.date)}</span>
                                  <div className="data-point-stats">
                                    <span>{dp.setsCompleted}×{dp.totalReps > 0 ? Math.round(dp.totalReps / dp.setsCompleted) : 0}</span>
                                    <span>{formatWeight(dp.maxWeight)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {summary.limited && (
                <div className="progression-limit-banner">
                  <span className="progression-limit-text">
                    Tracking <strong>{summary.maxTracked}</strong> of {summary.totalTracked} exercises.
                    Upgrade to track more.
                  </span>
                  <button className="progression-upgrade-btn" onClick={() => window.location.href = '/pricing'}>
                    Upgrade
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressionSection;
